
import { UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import type { Application } from '@/types/database.types';

interface BusinessOperationsProps {
  register: UseFormRegister<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
}

export const BusinessOperations = ({
  register,
  setValue,
  watch,
}: BusinessOperationsProps) => {
  const hasHeightsWork = watch('has_heights_work') as boolean | undefined;
  const hasBelowGroundWork = watch('has_below_ground_work') as boolean | undefined;
  const hasVehicleExposure = watch('has_vehicle_exposure') as boolean | undefined;
  const hasHazardousMaterials = watch('has_hazardous_materials') as boolean | undefined;

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor="has_heights_work" className="flex-1">
            Does the organization have employees who work at heights?
          </Label>
          <Switch
            id="has_heights_work"
            checked={hasHeightsWork || false}
            onCheckedChange={(checked) => {
              setValue('has_heights_work', checked);
            }}
          />
        </div>
        
        {hasHeightsWork && (
          <div className="space-y-4 pl-5 border-l-2 border-primary/20">
            <div className="space-y-2">
              <Label htmlFor="heights_work_details">Heights work description</Label>
              <Textarea
                id="heights_work_details"
                {...register('heights_work_details')}
                placeholder="Describe the work performed at heights..."
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="max_height">Maximum working height (in feet)</Label>
              <Input
                id="max_height"
                {...register('max_height')}
                type="text"
                placeholder="Enter the maximum working height"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="heights_safety_measures">Heights safety measures</Label>
              <Textarea
                id="heights_safety_measures"
                {...register('heights_safety_measures')}
                placeholder="Describe the safety measures for working at heights..."
              />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <Label htmlFor="has_below_ground_work" className="flex-1">
            Does the organization have employees who work below ground level?
          </Label>
          <Switch
            id="has_below_ground_work"
            checked={hasBelowGroundWork || false}
            onCheckedChange={(checked) => {
              setValue('has_below_ground_work', checked);
            }}
          />
        </div>
        
        {hasBelowGroundWork && (
          <div className="space-y-4 pl-5 border-l-2 border-primary/20">
            <div className="space-y-2">
              <Label htmlFor="below_ground_work_details">Below ground work description</Label>
              <Textarea
                id="below_ground_work_details"
                {...register('below_ground_work_details')}
                placeholder="Describe the work performed below ground..."
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="max_depth">Maximum depth (in feet)</Label>
              <Input
                id="max_depth"
                {...register('max_depth')}
                type="text"
                placeholder="Enter the maximum depth"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confined_space_procedures">Confined space entry procedures</Label>
              <Textarea
                id="confined_space_procedures"
                {...register('confined_space_procedures')}
                placeholder="Describe confined space entry procedures if applicable..."
              />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <Label htmlFor="has_vehicle_exposure" className="flex-1">
            Does the organization have employees who drive for work purposes?
          </Label>
          <Switch
            id="has_vehicle_exposure"
            checked={hasVehicleExposure || false}
            onCheckedChange={(checked) => {
              setValue('has_vehicle_exposure', checked);
            }}
          />
        </div>
        
        {hasVehicleExposure && (
          <div className="space-y-4 pl-5 border-l-2 border-primary/20">
            <div className="space-y-2">
              <Label htmlFor="vehicle_count">Number of vehicles operated</Label>
              <Input
                id="vehicle_count"
                {...register('vehicle_count')}
                type="text"
                placeholder="Enter the number of vehicles"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="driver_count">Number of employees who drive</Label>
              <Input
                id="driver_count"
                {...register('driver_count')}
                type="text"
                placeholder="Enter the number of drivers"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="vehicle_safety_program">Vehicle safety program details</Label>
              <Textarea
                id="vehicle_safety_program"
                {...register('vehicle_safety_program')}
                placeholder="Describe your vehicle safety program..."
              />
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <Label htmlFor="has_hazardous_materials" className="flex-1">
            Does the organization handle hazardous materials?
          </Label>
          <Switch
            id="has_hazardous_materials"
            checked={hasHazardousMaterials || false}
            onCheckedChange={(checked) => {
              setValue('has_hazardous_materials', checked);
            }}
          />
        </div>
        
        {hasHazardousMaterials && (
          <div className="space-y-2 pl-5 border-l-2 border-primary/20">
            <Label htmlFor="hazardous_materials_details">Hazardous materials details</Label>
            <Textarea
              id="hazardous_materials_details"
              {...register('hazardous_materials_details')}
              placeholder="Describe the hazardous materials handled and safety protocols..."
            />
          </div>
        )}
      </div>
    </div>
  );
};
